import React,{useState,useEffect} from "react";
import {C,fmt,today,gCalc,numToWords,CC,SC,NC,NI,dlCSV} from "../utils/constants";
import {Bdg,CB,Card,Btn,Inp,Sel,Modal,Bar,Row} from "./UI";




function _invoiceParts(o, co, myco={}) {
  const items = o.items || [];
  const isCash = o.bill_type === 'cash_bill';

  // Print settings
  const showItemDisc = myco.showDiscount !== false;
  const showFreeQty = myco.showFreeQty !== false;
  const showOverallDisc = myco.showOverallDisc !== false;
  const showMRP = myco.showMRP !== false;
  const showHSN = myco.showHSN !== false;
  
  // GST calculations
  const gstType = o.gt || o.gst_type || 'CGST_SGST';
  const isIGST = gstType === 'IGST';
  const rate = co?.gst || 18;
  
  // Item calculations
  const calcItems = items.map(it => {
    const name = it.product_name || it.name || '';
    const qty = +it.qty || 0;
    const price = +it.price || +it.sp || 0;
    const disc = +it.disc || 0;
    const discPct = +it.dp || 0;
    const grossAmt = (price - disc) * qty;
    const gstRate = +it.gst || rate;
    // If the product's price already includes GST, back-calculate the taxable value
    // instead of adding tax on top of it (which would double-charge the customer)
    const taxable = it.gst_inclusive ? grossAmt/(1+gstRate/100) : grossAmt;
    const cgstRate = isIGST ? 0 : gstRate/2;
    const igstRate = isIGST ? gstRate : 0;
    const cgstAmt = isIGST ? 0 : taxable * cgstRate/100;
    const igstAmt = isIGST ? taxable * igstRate/100 : 0;
    const total = taxable + cgstAmt + cgstAmt + igstAmt;
    return {...it, qty, price, disc, discPct, taxable, gstRate, cgstRate, igstRate, cgstAmt, igstAmt, total};
  });
  
  const subTotal = calcItems.reduce((s,i) => s + i.taxable, 0);
  const totalCGST = isIGST ? 0 : calcItems.reduce((s,i) => s + i.cgstAmt, 0);
  const totalSGST = totalCGST;
  const totalIGST = isIGST ? calcItems.reduce((s,i) => s + i.igstAmt, 0) : 0;
  const grandTotal = subTotal + totalCGST + totalSGST + totalIGST;
  const roundoff = Math.round(grandTotal) - grandTotal;
  const grandRound = Math.round(grandTotal);
  const totalQty = calcItems.reduce((s,i) => s + i.qty, 0);
  const due = grandRound - (+o.paid || 0);

  // HSN Summary
  const hsnMap = {};
  calcItems.forEach(it => {
    const h = it.hsn || '—';
    const r = it.gstRate || rate;
    const key = `${h}_${r}`;
    if(!hsnMap[key]) hsnMap[key] = {hsn:h, rate:r, taxable:0, cgst:0, sgst:0, igst:0};
    hsnMap[key].taxable += it.taxable;
    hsnMap[key].cgst += it.cgstAmt;
    hsnMap[key].sgst += it.cgstAmt;
    hsnMap[key].igst += it.igstAmt;
  });
  const hsnRows = Object.values(hsnMap);

  const fmt = n => (+n||0).toLocaleString('en-IN', {minimumFractionDigits:2, maximumFractionDigits:2});
  const fmtQ = n => (+n||0).toLocaleString('en-IN');
  
  const firmName = myco?.name || 'My Company';
  const firmAddr = myco?.address || '';
  const firmPhone = myco?.phone || '';
  const firmEmail = myco?.email || '';
  const firmGST = myco?.gstin || '';
  const firmPAN = myco?.pan || '';
  const firmBank = myco?.bank || '';
  const firmAcc = myco?.acc || '';
  const firmIFSC = myco?.ifsc || '';
  const firmUpi = myco?.upi || '';
  // A real, scannable UPI payment QR — standard "upi://pay" deep-link format that GPay/
  // PhonePe/Paytm all recognise, pre-filled with this invoice's exact amount so the
  // customer can just scan and confirm instead of typing the UPI ID and amount by hand.
  const upiQrUrl = firmUpi ? `https://api.qrserver.com/v1/create-qr-code/?size=110x110&data=${encodeURIComponent(`upi://pay?pa=${firmUpi}&pn=${encodeURIComponent(firmName||'')}&am=${grandRound}&cu=INR`)}` : '';
  const firmTerms1 = myco?.terms1 || 'Goods once sold will not be taken back.';
  const firmTerms2 = myco?.terms2 || 'Interest @18% p.a. will be charged if payment not made within 30 days.';
  const firmTerms3 = myco?.terms3 || '';

  // Item rows
  const itemRows = calcItems.map((it,i) => `
    <tr>
      <td class="c">${i+1}</td>
      <td>${it.product_name||it.name||'—'}${it.gst_inclusive?'<br/><span class="sub" style="color:#B45309">(price incl. GST)</span>':''}<br/><span class="sub">${it.brand||''}</span></td>
      ${showHSN?`<td class="c">${it.hsn||'—'}</td>`:''}
      <td class="c">${it.qty}${(showFreeQty&&(+it.free||0)>0)?` <span style="color:#059669;font-weight:700">+${it.free} Free</span>`:''} ${it.unit||'Pcs'}</td>
      ${showMRP?`<td class="r">${fmt(it.mrp||it.price)}</td>`:''}
      ${showItemDisc?`<td class="r">${it.discPct>0?it.discPct+'%':'—'}</td>`:''}
      <td class="r">${fmt(it.price)}</td>
      ${isIGST
        ? `<td class="r">${it.igstRate}%</td><td class="r">${fmt(it.igstAmt)}</td>`
        : `<td class="r">${it.cgstRate}%</td><td class="r">${fmt(it.cgstAmt)}</td><td class="r">${it.cgstRate}%</td><td class="r">${fmt(it.cgstAmt)}</td>`
      }
      <td class="r bold">${fmt(it.taxable + it.cgstAmt + it.cgstAmt + it.igstAmt)}</td>
    </tr>`).join('');

  // Pad the bill with blank rows so it always shows at least MIN_BILL_ROWS lines —
  // gives it the familiar look of a traditional paper bill book, and makes short
  // bills easier to read instead of looking cut-off after just 1-2 items.
  const MIN_BILL_ROWS = 10;
  const invColCount = 4 + (showHSN?1:0) + (showMRP?1:0) + (showItemDisc?1:0) + (isIGST?2:4) + 1;
  const invEmptyRowsNeeded = Math.max(0, MIN_BILL_ROWS - calcItems.length);
  const itemRowsPadded = itemRows + Array(invEmptyRowsNeeded).fill(0)
    .map(()=>`<tr><td colspan="${invColCount}" style="height:22px;">&nbsp;</td></tr>`).join('');

  // HSN summary rows
  const hsnSummaryRows = hsnRows.map(h => `
    <tr>
      <td>${h.hsn}</td><td class="r">${h.rate}%</td>
      <td class="r">${fmt(h.taxable)}</td>
      ${isIGST
        ? `<td class="r">${fmt(h.igst)}</td>`
        : `<td class="r">${fmt(h.cgst)}</td><td class="r">${fmt(h.sgst)}</td>`
      }
      <td class="r bold">${fmt(isIGST?h.igst:h.cgst+h.sgst)}</td>
    </tr>`).join('');

  const amountWords = (typeof numToWords === 'function') ? numToWords(grandRound) : '';

  const STYLES = `
    *{margin:0;padding:0;box-sizing:border-box}
    html,body{width:100%;height:100%}
    body{font-family:Arial,sans-serif;font-size:10px;color:#000;background:#f0f0f0}
    .page{width:210mm;min-height:297mm;margin:10mm auto;padding:8mm;border:1px solid #ccc;background:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.15)}
    @page{size:A4 portrait;margin:0}
    @media screen and (max-width:800px){
      body{background:#fff}
      .page{width:100% !important;min-height:auto;margin:0;padding:10px;border:none;box-shadow:none}
      .np button,.np select{font-size:13px !important;padding:10px 16px !important}
    }
    @media print{
      html,body{background:#fff!important;width:210mm!important;height:297mm!important}
      .page{width:210mm!important;min-height:297mm!important;margin:0!important;padding:8mm!important;border:none!important;box-shadow:none!important;page-break-after:always}
      .np{display:none!important}
    }
    .header{display:flex;align-items:flex-start;gap:10px;border-bottom:2px solid #1C2B3A;padding-bottom:8px;margin-bottom:8px}
    .logo{width:60px;height:60px;display:flex;align-items:center;justify-content:center;border:1px solid #ddd;font-size:20px;font-weight:900;color:#1C2B3A;border-radius:4px;flex-shrink:0}
    .firm-info{flex:1;text-align:center}
    .firm-name{font-size:20px;font-weight:900;color:#1C2B3A;letter-spacing:1px}
    .firm-sub{font-size:9px;color:#444;margin-top:2px}
    .inv-title{font-size:13px;font-weight:800;color:#1C2B3A;margin-top:4px;text-decoration:underline}
    .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #000;margin-bottom:6px}
    .info-box{padding:5px 8px;border-right:1px solid #000}
    .info-box:last-child{border-right:none}
    .info-row{display:flex;gap:4px;margin-bottom:2px;font-size:9px}
    .info-label{font-weight:700;color:#333;min-width:80px}
    .party-grid{display:grid;grid-template-columns:1fr 1fr;border:1px solid #000;margin-bottom:6px}
    .party-box{padding:6px 8px;border-right:1px solid #000}
    .party-box:last-child{border-right:none}
    .party-title{font-weight:800;font-size:9px;text-decoration:underline;margin-bottom:3px}
    .party-name{font-size:11px;font-weight:700;color:#1C2B3A}
    table{width:100%;border-collapse:collapse;margin-bottom:6px}
    th{background:#1C2B3A;color:#fff;padding:5px 4px;font-size:9px;font-weight:700;border:1px solid #000}
    td{padding:4px;border:1px solid #ccc;font-size:9px;vertical-align:middle}
    .c{text-align:center}
    .r{text-align:right}
    .bold{font-weight:700}
    .sub{font-size:8px;color:#666}
    .totals-section{display:flex;gap:6px;margin-bottom:6px}
    .hsn-box{flex:1;border:1px solid #000;padding:5px}
    .hsn-title{font-weight:800;font-size:9px;margin-bottom:3px}
    .total-box{width:200px;border:1px solid #000;padding:5px}
    .total-row{display:flex;justify-content:space-between;padding:2px 0;font-size:10px}
    .total-row.grand{font-size:12px;font-weight:900;border-top:2px solid #000;padding-top:4px;margin-top:3px}
    .words-box{border:1px solid #000;padding:5px 8px;margin-bottom:6px;font-size:9px}
    .bank-box{border:1px solid #000;padding:5px 8px;margin-bottom:6px;font-size:9px}
    .footer-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-top:6px}
    .terms-box{border:1px solid #ccc;padding:5px 8px;font-size:8px}
    .sign-box{border:1px solid #ccc;padding:5px 8px;text-align:right;font-size:9px}
    .sign-line{border-top:1px solid #000;margin-top:30px;padding-top:4px;font-weight:700}
    .np{background:#f5f5f5;padding:8px;text-align:center;margin-bottom:10px;border-radius:4px}

  `;

  const PAGE_HTML = `<div class="page">
    <!-- HEADER -->
    <div class="header">
      <div class="logo">${myco?.logo?`<img src="${myco.logo}" style="width:100%;height:100%;object-fit:contain;border-radius:4px"/>`:'🏢'}</div>
      <div class="firm-info">
        <div class="firm-name">${firmName.toUpperCase()}</div>
        <div class="firm-sub">${firmAddr}</div>
        <div class="firm-sub">Tel: ${firmPhone}${firmEmail?' | Email: '+firmEmail:''}</div>
        ${firmGST?`<div class="firm-sub">GSTIN: ${firmGST}${firmPAN?' | PAN: '+firmPAN:''}</div>`:''}
        <div class="inv-title">${isCash?'CASH BILL':'GST TAX INVOICE'}</div>
      </div>
    </div>

    <!-- INVOICE INFO -->
    <div class="info-grid">
      <div class="info-box">
        <div class="info-row"><span class="info-label">Invoice No.</span><span>: ${o.inv}</span></div>
        <div class="info-row"><span class="info-label">Date</span><span>: ${o.date}</span></div>
        <div class="info-row"><span class="info-label">Place of Supply</span><span>: ${co?.state||'Punjab'}</span></div>
        <div class="info-row"><span class="info-label">Reverse Charge</span><span>: N</span></div>
      </div>
      <div class="info-box">
        <div class="info-row"><span class="info-label">Transport</span><span>: ${o.transport||''}</span></div>
        <div class="info-row"><span class="info-label">E-Way Bill</span><span>: ${o.eway||''}</span></div>
        <div class="info-row"><span class="info-label">Cases</span><span>: ${o.cases||''}</span></div>
        <div class="info-row"><span class="info-label">Sales Man</span><span>: ${o.by||''}</span></div>
      </div>
    </div>

    <!-- PARTY DETAILS -->
    <div class="party-grid">
      <div class="party-box">
        <div class="party-title">Billed To:</div>
        <div class="party-name">${o.cust||''}</div>
        <div style="font-size:9px;margin-top:2px">${o.address||''}</div>
        <div style="font-size:9px">Ph: ${o.phone||''}</div>
        ${o.gstin?`<div style="font-size:9px">GSTIN: ${o.gstin}</div>`:''}
      </div>
      <div class="party-box">
        <div class="party-title">Shipped To:</div>
        <div class="party-name">${o.ship_name||o.cust||''}</div>
        <div style="font-size:9px">${o.ship_address||o.address||''}</div>
        <div style="font-size:9px">Ph: ${o.ship_phone||o.phone||''}</div>
      </div>
    </div>

    <!-- ITEMS TABLE -->
    <table>
      <thead>
        <tr>
          <th class="c" style="width:25px">S.N</th>
          <th>Description of Goods</th>
          ${showHSN?'<th class="c" style="width:55px">HSN/SAC</th>':''}
          <th class="c" style="width:50px">Qty/Unit</th>
          ${showMRP?'<th class="r" style="width:55px">MRP</th>':''}
          ${showItemDisc?'<th class="r" style="width:40px">Disc%</th>':''}
          <th class="r" style="width:55px">Rate</th>
          ${isIGST
            ? '<th class="r" style="width:35px">IGST%</th><th class="r" style="width:55px">IGST Amt</th>'
            : '<th class="r" style="width:35px">CGST%</th><th class="r" style="width:55px">CGST Amt</th><th class="r" style="width:35px">SGST%</th><th class="r" style="width:55px">SGST Amt</th>'
          }
          <th class="r" style="width:65px">Amount(₹)</th>
        </tr>
      </thead>
      <tbody>
        ${itemRowsPadded}
        <tr style="background:#f5f5f5">
          <td colspan="${2+(showHSN?1:0)}" class="bold">TOTAL</td>
          <td class="c bold">${fmtQ(totalQty)} Pcs</td>
          <td colspan="${(showMRP?1:0)+(showItemDisc?1:0)+1+(isIGST?2:4)}"></td>
          <td class="r bold">${fmt(grandTotal)}</td>
        </tr>
      </tbody>
    </table>

    <!-- HSN SUMMARY + TOTALS -->
    <div class="totals-section">
      <div class="hsn-box">
        <div class="hsn-title">HSN/SAC Tax Summary:</div>
        <table style="margin:0">
          <thead><tr>
            <th>HSN</th><th class="r">Rate</th><th class="r">Taxable Amt</th>
            ${isIGST?'<th class="r">IGST</th>':'<th class="r">CGST</th><th class="r">SGST</th>'}
            <th class="r">Total Tax</th>
          </tr></thead>
          <tbody>${hsnSummaryRows}</tbody>
        </table>
      </div>
      <div class="total-box">
        <div class="total-row"><span>Sub Total</span><span>₹${fmt(subTotal)}</span></div>
        ${isIGST
          ? `<div class="total-row"><span>IGST</span><span>₹${fmt(totalIGST)}</span></div>`
          : `<div class="total-row"><span>CGST</span><span>₹${fmt(totalCGST)}</span></div>
             <div class="total-row"><span>SGST</span><span>₹${fmt(totalSGST)}</span></div>`
        }
        <div class="total-row"><span>Roundoff</span><span>₹${fmt(roundoff)}</span></div>
        <div class="total-row"><span>CR/DR Note</span><span>₹0.00</span></div>
        <div class="total-row grand"><span>GRAND TOTAL</span><span>₹${fmt(grandRound)}</span></div>
        <div class="total-row" style="color:#DC2626"><span>Paid</span><span>₹${fmt(o.paid||0)}</span></div>
        ${due>0?`<div class="total-row bold" style="color:#DC2626"><span>Due</span><span>₹${fmt(due)}</span></div>`:''}
      </div>
    </div>

    <!-- AMOUNT IN WORDS -->
    <div class="words-box">
      <strong>Amount in Words:</strong> ${amountWords?amountWords+' Only':'Rs. '+grandRound+' Only'}
    </div>

    <!-- BANK DETAILS -->
    ${firmBank||firmUpi?`<div class="bank-box" style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <div>
        <strong>Bank Details:</strong>
        ${firmBank?`Account Name: ${firmName} | Account No.: ${firmAcc} | IFSC Code: ${firmIFSC} | Bank: ${firmBank}`:''}
        ${firmUpi?`<br/>UPI: ${firmUpi}`:''}
      </div>
      ${upiQrUrl?`<div style="text-align:center;flex-shrink:0;">
        <img src="${upiQrUrl}" width="80" height="80" style="display:block;"/>
        <div style="font-size:8px;color:#666;">Scan to Pay ₹${grandRound}</div>
      </div>`:''}
    </div>`:''}

    <!-- FOOTER -->
    <div class="footer-grid">
      <div class="terms-box">
        <strong>Terms & Conditions:</strong><br/>
        ${firmTerms1?firmTerms1+'<br/>':''}
        ${firmTerms2?firmTerms2+'<br/>':''}
        ${firmTerms3?firmTerms3:''}
      </div>
      <div class="sign-box">
        <div>For <strong>${firmName}</strong></div>
        <div style="margin-top:35px;text-align:center">
          <div style="border-top:1px solid #000;padding-top:4px;width:120px;margin:0 auto">Authorised Signatory</div>
        </div>
        <div style="margin-top:15px;border-top:1px dashed #ccc;padding-top:5px">
          <div>Receiver's Signature: _______________</div>
          <div style="margin-top:4px">Date: _______________</div>
        </div>
      </div>
    </div>

  </div>`;

  return { styles: STYLES, pageHtml: PAGE_HTML, isCash, invNo: o.inv||o.id||'' };
}

function pInv(o, co, myco={}) {
  const {styles, pageHtml, isCash, invNo} = _invoiceParts(o, co, myco);

  const w = window.open('about:blank', '_blank');
  if(!w){ alert('Popup blocked! Please allow popups for this site.'); return; }
  try{ w.moveTo(0,0); w.resizeTo(window.screen.availWidth,window.screen.availHeight); }catch(e){}

  w.document.write(`<!DOCTYPE html><html><head>
  <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=2">
  <meta charset="UTF-8">
  <title>${isCash?'Cash Bill':'Tax Invoice'} - ${invNo}</title>
  <style>${styles}</style></head><body>
  
  <div class="np" style="display:flex;align-items:center;justify-content:center;gap:8px;flex-wrap:wrap">
    <button onclick="window.print()" style="padding:8px 20px;background:#1C2B3A;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:700">🖨️ Print / Save as PDF</button>
    <select id="copySel" style="padding:8px 10px;border-radius:6px;border:1px solid #ccc;font-size:12px;font-family:inherit">
      <option value="1">1 Copy (Original)</option>
      <option value="2">2 Copies (Original + Duplicate)</option>
      <option value="3">3 Copies (Original + Duplicate + Triplicate)</option>
    </select>
    <button onclick="window.close()" style="padding:8px 14px;background:#666;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:12px">✕ Close</button>
  </div>

  ${pageHtml}

  </body></html>`);
  w.document.close();
  w.onload = () => setTimeout(()=>w.focus(), 200);
}

// ── Bulk Invoice Print — same exact bill format as pInv, multiple invoices ──
function pInvBulk(orders, gc, myco={}) {
  if(!orders || !orders.length){ alert('No orders to print!'); return; }

  const w = window.open('about:blank', '_blank');
  if(!w){ alert('Popup blocked! Please allow popups for this site.'); return; }
  try{ w.moveTo(0,0); w.resizeTo(window.screen.availWidth,window.screen.availHeight); }catch(e){}

  let styles = '';
  const pages = orders.map(o => {
    const co = (gc && gc(o.cid)) || {};
    const parts = _invoiceParts(o, co, myco);
    if(!styles) styles = parts.styles;
    return parts.pageHtml;
  }).join('\n');

  w.document.write(`<!DOCTYPE html><html><head>
  <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=2">
  <meta charset="UTF-8">
  <title>Bulk Invoice Print (${orders.length} invoices)</title>
  <style>${styles}</style></head><body>

  <div class="np" style="display:flex;align-items:center;justify-content:center;gap:8px;flex-wrap:wrap">
    <button onclick="window.print()" style="padding:8px 20px;background:#1C2B3A;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:700">🖨️ Print All (${orders.length})</button>
    <button onclick="window.close()" style="padding:8px 14px;background:#666;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:12px">✕ Close</button>
  </div>

  ${pages}

  </body></html>`);
  w.document.close();
  w.onload = () => setTimeout(()=>w.focus(), 200);
}

function pPur(p, myco={}) {
  const firmName = myco?.name || "My Company";
  const firmPhone = myco?.phone || "";
  const firmAddr = myco?.address || "";
  const firmGST = myco?.gstin || "";
  const w = window.open("","_blank","width=900,height=700,scrollbars=yes");
  if(!w){ alert("Popup blocked! Please allow popups in your browser settings."); return; }
  try{ w.moveTo(0,0); w.resizeTo(window.screen.availWidth,window.screen.availHeight); }catch(e){}
  const items = p.items||[];
  const itemRows = items.map((it,i)=>`
    <tr>
      <td style="padding:5px;border:1px solid #ddd;text-align:center">${i+1}</td>
      <td style="padding:5px;border:1px solid #ddd">${it.product_name||it.name||""}</td>
      <td style="padding:5px;border:1px solid #ddd">${it.hsn||""}</td>
      <td style="padding:5px;border:1px solid #ddd;text-align:center">${it.qty||0}</td>
      <td style="padding:5px;border:1px solid #ddd;text-align:right">₹${(+it.cp||+it.cost||0).toFixed(2)}</td>
      <td style="padding:5px;border:1px solid #ddd;text-align:right">₹${((+it.cp||+it.cost||0)*(+it.qty||0)).toFixed(2)}</td>
    </tr>`).join("");
  // Pad with blank rows so the bill always shows at least 10 lines — traditional bill-book look
  const purMinRows=10;
  const purEmptyRows=Array(Math.max(0,purMinRows-items.length)).fill(0)
    .map(()=>`<tr><td colspan="6" style="height:22px;border:1px solid #ddd;">&nbsp;</td></tr>`).join("");
  const itemRowsPadded=itemRows+purEmptyRows;
  w.document.write(`<!DOCTYPE html><html><head>
  <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=2"><title>Purchase Bill - ${p.id}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    html,body{width:100%;background:#f0f0f0}
    body{font-family:Arial,sans-serif;font-size:11px;color:#000;padding:0}
    .page-wrap{width:210mm;margin:10mm auto;padding:12mm;background:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.15)}
    @page{size:A4 portrait;margin:0}
    @media print{html,body{background:#fff!important}.page-wrap{width:210mm!important;margin:0!important;box-shadow:none!important}.np{display:none!important}}
    .header{text-align:center;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:10px}
    .title{font-size:20px;font-weight:900;color:#1C2B3A}
    .subtitle{font-size:11px;color:#555}
    .bill-info{display:flex;justify-content:space-between;margin-bottom:10px;font-size:11px}
    table{width:100%;border-collapse:collapse;margin-bottom:10px}
    th{background:#1C2B3A;color:#fff;padding:7px;border:1px solid #ccc;font-size:11px}
    .totals{display:flex;flex-direction:column;align-items:flex-end;gap:4px}
    .tot-row{display:flex;gap:20px;justify-content:flex-end;font-size:12px}
    .grand{font-size:14px;font-weight:900;border-top:2px solid #000;padding-top:5px}
    @media print{.np{display:none}}
  </style></head><body>
  <div class="page-wrap">
  <div class="np" style="padding:8px;background:#f0f0f0;text-align:center;margin-bottom:10px">
    <button onclick="window.print()" style="padding:8px 20px;background:#1C2B3A;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:12px;font-weight:700">🖨️ Print</button>
    <button onclick="window.close()" style="padding:8px 16px;background:#666;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:12px;margin-left:8px">✕ Close</button>
  </div>
  <div class="header">
    <div class="title">${firmName}</div>
    <div class="subtitle">${firmAddr} | ${firmPhone} | GSTIN: ${firmGST}</div>
    <div style="font-size:13px;font-weight:700;margin-top:5px">PURCHASE BILL</div>
  </div>
  <div class="bill-info">
    <div>
      <b>Supplier:</b> ${p.supp||"-"}<br/>
      <b>Supp. Invoice:</b> ${p.supp_inv_no||p.suppInvNo||"-"}<br/>
    </div>
    <div style="text-align:right">
      <b>Bill No:</b> ${p.id}<br/>
      <b>Date:</b> ${p.date}<br/>
    </div>
  </div>
  <table>
    <thead><tr>
      <th>#</th><th>Product</th><th>HSN</th><th>Qty</th><th>Rate</th><th>Amount</th>
    </tr></thead>
    <tbody>${itemRowsPadded}</tbody>
  </table>
  <div class="totals">
    <div class="tot-row"><span>Subtotal:</span><span>₹${(+p.sub||+p.subtotal||0).toFixed(2)}</span></div>
    <div class="tot-row"><span>GST:</span><span>₹${(+p.gstAmt||+p.gst||0).toFixed(2)}</span></div>
    <div class="tot-row grand"><span>Total:</span><span>₹${(+p.total||0).toFixed(2)}</span></div>
    <div class="tot-row"><span>Paid:</span><span>₹${(+p.paid||0).toFixed(2)}</span></div>
    <div class="tot-row" style="color:red"><span>Due:</span><span>₹${((+p.total||0)-(+p.paid||0)).toFixed(2)}</span></div>
  </div>
  </body></html>`);
  w.document.close();
}


// ── CR/DR Note + Returns PDF Print ────────────────────────────────
function pNote(note, myco={}) {
  const firmName = myco?.name || "My Company";
  const firmPhone = myco?.phone || "";
  const firmAddr = myco?.address || "";
  const firmGST = myco?.gstin || "";
  const noteType = note.nt || note.type || "Credit Note";
  const noteNo = note.noteNo || note.note_no || "-";
  const w = window.open("","_blank","width=900,height=700,scrollbars=yes");
  if(!w){ alert("Popup blocked! Please allow popups in your browser settings."); return; }
  try{ w.moveTo(0,0); w.resizeTo(window.screen.availWidth,window.screen.availHeight); }catch(e){}

  const typeColors = {
    "Credit Note": "#16A34A",
    "Debit Note": "#DC2626",
    "Sale Return": "#2563EB",
    "Purchase Return": "#7C3AED",
  };
  const color = typeColors[noteType] || "#1C2B3A";
  const items = note.items || [];
  const itemRows = items.map((it,i) => `
    <tr>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:center">${i+1}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee">${it.product_name||it.name||""}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee">${it.hsn||""}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:center">${it.qty||0}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:right">₹${(+it.price||0).toFixed(2)}</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:right">₹${((+it.price||0)*(+it.qty||0)).toFixed(2)}</td>
    </tr>`).join("");
  // Pad with blank rows (only when itemized — not the direct-amount case below)
  const noteMinRows=10;
  const noteEmptyRows=Array(Math.max(0,noteMinRows-items.length)).fill(0)
    .map(()=>`<tr><td colspan="6" style="height:22px;border-bottom:1px solid #eee;">&nbsp;</td></tr>`).join("");
  const itemRowsPadded=itemRows+noteEmptyRows;

  // If no items, show amount directly
  const amountSection = items.length === 0 ? `
    <tr>
      <td colspan="5" style="padding:5px 8px;border-bottom:1px solid #eee">Amount</td>
      <td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:right">₹${(+note.taxable||0).toFixed(2)}</td>
    </tr>` : itemRowsPadded;

  w.document.write(`<!DOCTYPE html><html><head>
  <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=2"><title>${noteType} - ${noteNo}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;font-size:11px;color:#000;background:#fff;padding:15px}
    .header{border-bottom:3px solid ${color};padding-bottom:10px;margin-bottom:12px}
    .firm-name{font-size:18px;font-weight:900;color:#1C2B3A}
    .note-type{font-size:14px;font-weight:800;color:${color};margin-top:4px}
    .info-grid{display:flex;justify-content:space-between;margin-bottom:12px;font-size:11px}
    table{width:100%;border-collapse:collapse;margin-bottom:12px}
    th{background:${color};color:#fff;padding:7px 8px;text-align:left;font-size:10px}
    .totals{display:flex;flex-direction:column;align-items:flex-end;gap:3px;font-size:11px}
    .tot-row{display:flex;gap:20px}
    .grand{font-size:13px;font-weight:900;border-top:2px solid #000;padding-top:5px;margin-top:5px}
    .stamp{margin-top:30px;text-align:right;font-size:10px;color:#666}
    @media print{.np{display:none}}
  </style></head><body>
  <div class="np" style="margin-bottom:10px">
    <button onclick="window.print()" style="padding:8px 20px;background:${color};color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:12px;font-weight:700;margin-right:8px">🖨️ Print / Save PDF</button>
    <button onclick="window.close()" style="padding:8px 14px;background:#666;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:12px">✕ Close</button>
  </div>
  <div class="header">
    <div class="firm-name">${firmName}</div>
    <div style="font-size:10px;color:#555;margin-top:2px">${firmAddr} | Ph: ${firmPhone} | GSTIN: ${firmGST}</div>
    <div class="note-type">${noteType.toUpperCase()}</div>
  </div>
  <div class="info-grid">
    <div>
      <div><b>Party:</b> ${note.party||"-"}</div>
      <div><b>Note No:</b> ${noteNo}</div>
      <div><b>Ref Invoice:</b> ${note.refInv||note.ref_invoice||"-"}</div>
    </div>
    <div style="text-align:right">
      <div><b>Date:</b> ${note.date||"-"}</div>
      <div><b>GST Type:</b> ${note.gt||note.gst_type||"CGST/SGST"}</div>
      <div><b>Status:</b> ${note.status||"Issued"}</div>
    </div>
  </div>
  ${note.reason?`<div style="margin-bottom:10px;padding:6px 10px;background:#F8FAFC;border-radius:6px;font-size:10px"><b>Reason:</b> ${note.reason}</div>`:""}
  <table>
    <thead><tr><th>#</th><th>Description</th><th>HSN</th><th>Qty</th><th>Rate</th><th>Amount</th></tr></thead>
    <tbody>${amountSection}</tbody>
  </table>
  <div class="totals">
    <div class="tot-row"><span>Taxable Amount:</span><span>₹${(+note.taxable||0).toFixed(2)}</span></div>
    <div class="tot-row"><span>GST:</span><span>₹${(+note.gst||+note.gst_amt||0).toFixed(2)}</span></div>
    <div class="tot-row grand"><span>Total ${noteType} Amount:</span><span style="color:${color}">₹${(+note.total||0).toFixed(2)}</span></div>
  </div>
  <div class="stamp">
    <div>For ${firmName}</div>
    <div style="margin-top:30px;border-top:1px solid #ccc;padding-top:5px;width:150px;text-align:center">Authorised Signatory</div>
  </div>
  </body></html>`);
  w.document.close();
}


// ── Payment Receipt PDF + Share ──────────────────────────────────
function pReceipt(payment, myco={}) {
  const firmName = myco?.name || "My Company";
  const firmPhone = myco?.phone || "";
  const firmAddr = myco?.address || "";
  const firmGST = myco?.gstin || "";

  const receiptNo = payment.id || "RCP-"+Date.now();
  const amount = +payment.amt || +payment.amount || 0;
  const method = payment.method || "Cash";
  const date = payment.date || new Date().toLocaleDateString("en-IN");
  const custName = payment.cust || "-";
  const invRef = payment.inv || payment.oid || "-";
  const phone = (payment.phone || "").replace(/[^0-9]/g, "");
  const due = +payment.due || 0;

  const html = `<!DOCTYPE html><html><head>
  <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=2">
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1">
  <title>Receipt ${receiptNo}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;background:#f0f0f0;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;min-height:100vh;padding:12px;gap:12px}
    .receipt{width:100%;max-width:380px;background:#fff;padding:20px;border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.15)}
    .firm{text-align:center;padding-bottom:12px;margin-bottom:12px;border-bottom:2px dashed #ddd}
    .firm-name{font-size:18px;font-weight:900;color:#1C2B3A}
    .firm-sub{font-size:11px;color:#666;margin-top:2px}
    .badge{display:inline-block;background:#16A34A;color:#fff;font-size:12px;font-weight:700;padding:4px 14px;border-radius:12px;margin-bottom:10px}
    .row{display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px dotted #eee;font-size:13px}
    .row-label{color:#666}
    .row-value{font-weight:600;color:#1C2B3A;text-align:right;max-width:180px}
    .amount-box{background:linear-gradient(135deg,#1C2B3A,#2563EB);border-radius:10px;padding:16px;margin:14px 0;display:flex;justify-content:space-between;align-items:center}
    .amount-label{color:rgba(255,255,255,0.8);font-size:13px;font-weight:600}
    .amount-value{color:#fff;font-size:26px;font-weight:900}
    .footer{text-align:center;color:#999;font-size:11px;margin-top:10px;padding-top:10px;border-top:1px dashed #ddd}
    .btn-row{display:flex;gap:10px;width:100%;max-width:380px}
    .btn{flex:1;padding:14px;border:none;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif}
    .btn-print{background:#1C2B3A;color:#fff}
    .btn-share{background:#25D366;color:#fff}
    @media print{
      body{background:#fff;padding:0}
      .receipt{box-shadow:none;border-radius:0;width:100%;max-width:100%;padding:10px}
      .btn-row{display:none}
    }
  </style>
  </head><body>

  <div class="receipt">
    <div class="firm">
      <div class="firm-name">${firmName}</div>
      ${firmAddr?`<div class="firm-sub">${firmAddr}</div>`:""}
      <div class="firm-sub">${firmPhone?"📞 "+firmPhone:""} ${firmGST?"| GSTIN: "+firmGST:""}</div>
    </div>
    <div style="text-align:center;margin-bottom:10px"><span class="badge">✅ PAYMENT RECEIPT</span></div>
    <div class="row"><span class="row-label">Receipt No</span><span class="row-value">${receiptNo}</span></div>
    <div class="row"><span class="row-label">Date</span><span class="row-value">${date}</span></div>
    <div class="row"><span class="row-label">Customer</span><span class="row-value">${custName}</span></div>
    <div class="row"><span class="row-label">Invoice Ref</span><span class="row-value">${invRef}</span></div>
    <div class="row"><span class="row-label">Method</span><span class="row-value">${method}</span></div>
    ${payment.note?`<div class="row"><span class="row-label">Note</span><span class="row-value">${payment.note}</span></div>`:""}
    <div class="amount-box">
      <span class="amount-label">Amount Paid</span>
      <span class="amount-value">₹${amount.toLocaleString("en-IN")}</span>
    </div>
    <div class="footer">Thank you for your payment! 🙏<br/><span style="font-size:9px">Computer generated receipt</span></div>
  </div>

  <div class="btn-row">
    <button class="btn btn-print" onclick="window.print()">🖨️ Print</button>
    <button class="btn btn-share" id="pdfBtn" onclick="downloadPDF()">📄 Download PDF</button>
  </div>

  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
  <script>
    async function downloadPDF() {
      const btn = document.getElementById('pdfBtn');
      btn.textContent = '⏳ Generating...';
      btn.disabled = true;

      try {
        // Hide buttons before screenshot
        document.querySelector('.btn-row').style.display = 'none';

        // Capture receipt as canvas
        const canvas = await html2canvas(document.querySelector('.receipt'), {
          scale: 3,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false
        });

        // Show buttons again
        document.querySelector('.btn-row').style.display = 'flex';

        const imgData = canvas.toDataURL('image/png');
        const { jsPDF } = window.jspdf;

        // A4 size PDF, receipt centered
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();

        // Scale image to fit page width with margins
        const imgW = pageW - 40; // 20mm margin each side
        const imgH = (canvas.height * imgW) / canvas.width;
        const xPos = 20;
        const yPos = 20;

        pdf.addImage(imgData, 'PNG', xPos, yPos, imgW, imgH);
        const fileName = 'Receipt_${custName}_${receiptNo}.pdf';

        // Mobile: share PDF file if possible
        if(navigator.share && navigator.canShare) {
          const pdfBlob = pdf.output('blob');
          const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
          if(navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                title: 'Payment Receipt — ${custName}',
                text: '💰 ₹${amount.toLocaleString("en-IN")} received',
                files: [file]
              });
              btn.textContent = '📄 Download PDF';
              btn.disabled = false;
              return;
            } catch(e) {
              if(e.name === 'AbortError') {
                btn.textContent = '📄 Download PDF';
                btn.disabled = false;
                return;
              }
            }
          }
        }

        // Fallback: direct download
        pdf.save(fileName);
        btn.textContent = '📄 Download PDF';
        btn.disabled = false;

      } catch(e) {
        document.querySelector('.btn-row').style.display = 'flex';
        alert('PDF generation failed: ' + e.message);
        btn.textContent = '📄 Download PDF';
        btn.disabled = false;
      }
    }
  </script>
  </body></html>`;

  const w = window.open("about:blank", "_blank");
  if(!w){ alert("Popup blocked! Please allow popups in your browser."); return; }
  try{ w.moveTo(0,0); w.resizeTo(window.screen.availWidth,window.screen.availHeight); }catch(e){}
  w.document.write(html);
  w.document.close();
}



function AddProdToInvoice({eo,setEo,co}){
  const [show,setShow]=React.useState(false);
  const [srch,setSrch]=React.useState("");
  const [allProds,setAllProds]=React.useState([]);

  React.useEffect(()=>{
    // Load products from MySQL when needed
    const tok=localStorage.getItem("gd_token");
    fetch("https://api.dawntoduskpro.com/api/products",{headers:{Authorization:"Bearer "+tok}})
      .then(r=>r.json())
      .then(d=>{
        if(Array.isArray(d))setAllProds(d.map(p=>({...p,cid:p.company_id||p.cid,sp:+p.sp||+p.sale_price||0,mrp:+p.mrp||0,gst:+p.gst||+p.gst_rate||18})));
      })
      .catch(()=>{});
  },[]);

  const filtered=allProds.filter(p=>(!srch||p.name.toLowerCase().includes(srch.toLowerCase())));

  const addItem=p=>{
    const newItem={id:p.id,name:p.name,hsn:p.hsn||"33059090",mrp:p.mrp||0,price:p.sp||0,qty:1,dp:0,disc:0,gst:p.gst||18};
    setEo({...eo,items:[...eo.items,newItem]});
    setShow(false);setSrch("");
  };

  const addManual=()=>{
    setEo({...eo,items:[...eo.items,{id:Date.now(),name:"",hsn:"33059090",mrp:0,price:0,qty:1,dp:0,disc:0,gst:18}]});
    setShow(false);
  };

  return <div style={{background:"#F0F9FF",borderRadius:9,padding:"9px 11px",marginBottom:10,border:`1.5px dashed ${C.primary}`}}>
    {!show?<button onClick={()=>setShow(true)} style={{width:"100%",padding:"7px",background:C.primary,color:"#fff",border:"none",borderRadius:7,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>➕ Naya Item Add Karo Invoice Mein</button>
    :<div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
        <div style={{fontSize:11,fontWeight:700,color:C.primary}}>📦 Product Select Karo</div>
        <button onClick={()=>setShow(false)} style={{background:"transparent",border:"none",color:C.sub,cursor:"pointer",fontSize:14}}>✕</button>
      </div>
      <input value={srch} onChange={e=>setSrch(e.target.value)} placeholder="Product search..." autoFocus style={{width:"100%",padding:"6px 9px",borderRadius:6,border:`1px solid ${C.border}`,fontSize:11,marginBottom:7,boxSizing:"border-box",fontFamily:"inherit"}}/>
      <div style={{maxHeight:150,overflowY:"auto",background:"#fff",borderRadius:6}}>
        {filtered.length===0?<div style={{padding:10,textAlign:"center",color:C.sub,fontSize:10}}>Koi product nahi mila</div>
        :filtered.slice(0,10).map(p=><div key={p.id} onClick={()=>addItem(p)} style={{padding:"6px 9px",borderBottom:`1px solid ${C.border}`,cursor:"pointer",fontSize:10,display:"flex",justifyContent:"space-between"}}>
          <span style={{fontWeight:600}}>{p.name}</span>
          <span style={{color:C.success}}>₹{p.sp} (Stock: {p.stock})</span>
        </div>)}
      </div>
      <button onClick={addManual} style={{width:"100%",marginTop:6,padding:"5px",background:"transparent",border:`1px dashed ${C.sub}`,color:C.sub,borderRadius:5,cursor:"pointer",fontSize:10,fontFamily:"inherit"}}>+ Manual Item (Custom Product)</button>
    </div>}
  </div>;
}

function InvModal({order,co,isAdmin,onClose,onSave,myco={}}){
  const [edit,setEdit]=useState(false);
  // Normalize item fields — server returns DB column names (product_name, discount, disc_pct)
  // while the UI expects (name, disc, dp). Map them here once so the rest of the component is safe.
  const normItems=(arr)=>(arr||[]).map(i=>({
    ...i,
    name: i.name||i.product_name||"",
    disc: i.disc!==undefined?i.disc:(i.discount||0),
    dp: i.dp!==undefined?i.dp:(i.disc_pct||0),
  }));
  const [eo,setEo]=useState({...order,items:normItems(order.items)});
  const o=edit?eo:{...order,items:normItems(order.items)};
  const items=o.items||[];
  const itot=items.reduce((s,i)=>s+(i.price-i.disc)*i.qty,0);
  const odiscAmt=o.odisc||0;
  const odp2=itot?(odiscAmt/itot*100):0;
  // Per item: back-calculate taxable value when the product's price already includes GST,
  // instead of always treating price as GST-exclusive (which double-charges inclusive items)
  const itemCalc=items.map(i=>{
    const itemNet=(i.price-i.disc)*i.qty;
    const itemNetAfterOdisc=itemNet-(itemNet*odp2/100);
    const rate2=i.gst||18;
    const itemTaxable=i.gst_inclusive ? itemNetAfterOdisc/(1+rate2/100) : itemNetAfterOdisc;
    const itemGst=i.gst_inclusive ? (itemNetAfterOdisc-itemTaxable) : (itemTaxable*rate2/100);
    return {taxable:itemTaxable, gst:itemGst};
  });
  const tax=itemCalc.reduce((s,i)=>s+i.taxable,0);
  const gstAmt2=itemCalc.reduce((s,i)=>s+i.gst,0);
  const g=o.gt==="IGST" ? {c:0,s:0,i:gstAmt2,t:gstAmt2} : {c:gstAmt2/2,s:gstAmt2/2,i:0,t:gstAmt2};
  const uniqueRates2=[...new Set(items.map(i=>i.gst||18))];
  const rateLabel2=uniqueRates2.length===1?uniqueRates2[0]:"Mixed";
  const grand=tax+g.t;const due=grand-(o.paid||0);const cc=co?.color||C.primary;
  const upd=(idx,f,v)=>{const its=[...eo.items];its[idx]={...its[idx],[f]:+v};if(f==="dp")its[idx].disc=Math.round(its[idx].price*(+v)/100);setEo({...eo,items:its});};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:12,overflowY:"auto"}}>
      <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:680,marginTop:4,boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
        <div style={{display:"flex",gap:7,padding:"11px 14px",borderBottom:`1px solid ${C.border}`,alignItems:"center",flexWrap:"wrap",background:"#F8FAFC",borderRadius:"16px 16px 0 0"}}>
          <CB co={co}/><div style={{fontSize:13,fontWeight:700,flex:1}}>{o.inv}</div>
          {!edit&&isAdmin&&<Btn ch="✏️ Edit" oc={()=>setEdit(true)} color={C.info} sm/>}
          {edit&&<Btn ch="💾 Save" oc={()=>{onSave(eo);setEdit(false);}} color={C.success} sm/>}
          {edit&&<Btn ch="✕ Cancel" oc={()=>setEdit(false)} color={C.sub} out sm/>}
          {!edit&&isAdmin&&<Btn ch="🖨️ Print" oc={()=>pInv(o,co,myco||{})} color={cc} sm/>}
          {!edit&&<Btn ch="📲 WA" oc={()=>waShare(o,co)} color="#25D366" sm/>}
          <Btn ch="✕ Close" oc={onClose} color={C.sub} out sm/>
        </div>
        <div style={{padding:"14px 18px"}}>
          <div style={{display:"flex",justifyContent:"space-between",borderBottom:`3px solid ${cc}`,paddingBottom:10,marginBottom:12}}>
            <div><div style={{fontSize:15,fontWeight:800,color:cc}}>{co?.name}</div><div style={{fontSize:10,color:C.sub}}>{co?.addr} • 📞{co?.phone}</div><div style={{fontSize:10,fontWeight:600}}>GSTIN: {co?.gstin}</div></div>
            <div style={{textAlign:"right"}}><div style={{fontSize:13,fontWeight:800,color:cc}}>TAX INVOICE</div><div style={{fontSize:11,color:C.sub}}>No: {o.inv}</div><div style={{fontSize:11,color:C.sub}}>{o.date}</div></div>
          </div>
          <div style={{display:"flex",gap:9,marginBottom:12}}>
            <div style={{flex:1,background:"#F8FAFC",padding:"9px 11px",borderRadius:9,fontSize:12}}>
              <div style={{fontWeight:700,color:C.sub,marginBottom:3}}>BILL TO</div>
              <div style={{fontWeight:700}}>{o.cust}</div><div style={{color:C.sub}}>📞{o.phone}</div>{o.cgstin&&<div style={{color:C.info,fontSize:10}}>GSTIN:{o.cgstin}</div>}
            </div>
            <div style={{flex:1,background:"#F8FAFC",padding:"9px 11px",borderRadius:9,fontSize:11}}>
              <div style={{fontWeight:700,color:C.sub,marginBottom:3}}>PAYMENT</div>
              <div>UPI: {co?.upi}</div><div>Bank: {co?.bank} | {co?.acc}</div><div>IFSC: {co?.ifsc}</div>
            </div>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,marginBottom:10}}>
            <thead><tr style={{background:cc}}>{["#","Product","HSN","MRP","Qty","Rate","Disc%","Amount",edit?"✕":""].map(h=><th key={h} style={{padding:"6px",color:"#fff",textAlign:h==="#"||h==="Product"||h==="HSN"?"left":"right",fontWeight:600}}>{h}</th>)}</tr></thead>
            <tbody>{items.map((it,i)=>{const net=(it.price-it.disc)*it.qty;return(
              <tr key={i} style={{background:i%2===0?"#F8FAFC":"#fff"}}>
                <td style={{padding:"5px",borderBottom:`1px solid ${C.border}`}}>{i+1}</td>
                <td style={{padding:"5px",borderBottom:`1px solid ${C.border}`}}>{edit?<input value={it.name||it.product_name||""} onChange={e=>{const its=[...eo.items];its[i]={...its[i],name:e.target.value};setEo({...eo,items:its});}} style={{width:"100%",fontSize:11,border:`1px solid ${C.border}`,borderRadius:4,padding:"2px 5px"}}/>:(it.name||it.product_name||"—")}</td>
                <td style={{padding:"5px",borderBottom:`1px solid ${C.border}`,color:C.sub}}>{it.hsn}</td>
                <td style={{padding:"5px",borderBottom:`1px solid ${C.border}`,textAlign:"right",color:C.light,textDecoration:"line-through"}}>{it.mrp?`₹${it.mrp}`:"—"}</td>
                <td style={{padding:"5px",borderBottom:`1px solid ${C.border}`,textAlign:"right"}}>{edit?<input type="number" value={it.qty} onChange={e=>upd(i,"qty",+e.target.value)} style={{width:38,fontSize:11,border:`1px solid ${C.border}`,borderRadius:4,padding:"2px",textAlign:"right"}}/>:it.qty}</td>
                <td style={{padding:"5px",borderBottom:`1px solid ${C.border}`,textAlign:"right"}}>{edit?<input type="number" value={it.price} onChange={e=>upd(i,"price",+e.target.value)} style={{width:55,fontSize:11,border:`1px solid ${C.border}`,borderRadius:4,padding:"2px",textAlign:"right"}}/>:`₹${it.price}`}</td>
                <td style={{padding:"5px",borderBottom:`1px solid ${C.border}`,textAlign:"right",color:C.danger}}>{edit?<input type="number" value={it.dp||0} onChange={e=>upd(i,"dp",+e.target.value)} style={{width:38,fontSize:11,border:`1px solid ${C.border}`,borderRadius:4,padding:"2px",textAlign:"right"}}/>:`${it.dp||0}%`}</td>
                <td style={{padding:"5px",borderBottom:`1px solid ${C.border}`,textAlign:"right",fontWeight:700}}>₹{fmt(net)}</td>
                {edit&&<td style={{padding:"3px"}}><button onClick={()=>setEo({...eo,items:eo.items.filter((_,j)=>j!==i)})} style={{background:C.danger,border:"none",color:"#fff",width:18,height:18,borderRadius:"50%",cursor:"pointer",fontSize:10}}>×</button></td>}
              </tr>
            );})}
            </tbody>
          </table>

          {/* Add Product in Edit Mode */}
          {edit&&<AddProdToInvoice eo={eo} setEo={setEo} co={co}/>}

          <div style={{display:"flex",gap:12,justifyContent:"flex-end",flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:160,background:"#F8FAFC",borderRadius:9,padding:"10px 12px",fontSize:11}}>
              <div style={{fontWeight:700,marginBottom:6}}>📊 GST Breakdown</div>
              {items.map((it,i)=>{const tx=(it.price-it.disc)*it.qty;const itemRate=it.gst||18;const itemTx=it.gst_inclusive?tx/(1+itemRate/100):tx;const ig=Math.round(it.gst_inclusive?(tx-itemTx):(itemTx*itemRate/100));return <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"2px 0",borderBottom:`1px solid ${C.border}`,fontSize:10}}><span style={{color:C.sub,flex:1}}>{(it.name||it.product_name||"Item").slice(0,15)}...{it.gst_inclusive?" (incl.)":""}</span><span>₹{fmt(tx)}</span><span style={{color:cc,fontWeight:700,marginLeft:4}}>{itemRate}%</span><span style={{color:C.success,marginLeft:4}}>₹{fmt(ig)}</span></div>;})}
              <div style={{marginTop:5,fontSize:10,color:C.sub,fontWeight:600}}>{o.gt==="CGST_SGST"?`CGST ${rateLabel2==="Mixed"?"Mixed":rateLabel2/2}%: ₹${fmt(g.c)} | SGST ${rateLabel2==="Mixed"?"Mixed":rateLabel2/2}%: ₹${fmt(g.s)}`:`IGST ${rateLabel2}%: ₹${fmt(g.i)}`}</div>
            </div>
            <div style={{minWidth:185,fontSize:12}}>
              <div style={{display:"flex",justifyContent:"space-between",padding:"3px 0",borderBottom:`1px solid ${C.border}`}}><span style={{color:C.sub}}>Taxable</span><span>₹{fmt(tax)}</span></div>
              {g.c>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"2px 0",color:C.sub}}><span>CGST {rateLabel2==="Mixed"?"Mixed":rateLabel2/2}%</span><span>₹{fmt(g.c)}</span></div>}
              {g.s>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"2px 0",color:C.sub}}><span>SGST {rateLabel2==="Mixed"?"Mixed":rateLabel2/2}%</span><span>₹{fmt(g.s)}</span></div>}
              {g.i>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"2px 0",color:C.sub}}><span>IGST {rateLabel2}%</span><span>₹{fmt(g.i)}</span></div>}
              <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontWeight:800,fontSize:15,color:cc,borderTop:`2px solid ${cc}`,marginTop:3}}><span>Grand Total</span><span>₹{fmt(grand)}</span></div>
              <div style={{fontWeight:700,color:due>0?C.danger:C.success}}>{due>0?`Due: ₹${fmt(due)}`:"✓ FULLY PAID"}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── New Order Full Page ─────────────────────────────────────────────

function waShare(o, co={}) {
  const msg = encodeURIComponent(
    "🧾 *Invoice: " + (o.inv||o.id) + "*\n👤 " + (o.cust||o.customer_name) + "\n📅 " + o.date + "\n💰 Total: ₹" + (+o.total||0).toLocaleString("en-IN")
  );
  const phone = (o.phone||o.customer_phone||"").replace(/\D/g,"");
  window.open(phone ? "https://wa.me/91"+phone+"?text="+msg : "https://wa.me/?text="+msg, "_blank");
}

export {pInv, pInvBulk, pPur, pNote, pReceipt, waShare, AddProdToInvoice, InvModal};
